import React, { ReactNode } from 'react';
import {
  Box,
  Checkbox,
  InputLabel,
  TextField,
  Typography
} from '@p81-common/ui';
import { useTranslation } from 'react-i18next';
import { MaskedTextField, WarningAttentionBox } from '../../../shared/ui';
import {
  isClientIdValid,
  isSecretKeyValid,
  isUuidValid
} from '../../../shared/CredentialsFields/helpers';
import { useTouchedFields } from '../../../shared/hooks';
import {
  OverrideCredentials,
  OverrideCredentialField
} from '../../helpers/overrideCredentials';
import { createClasses } from './styles';

const CREDENTIALS =
  'tenantsAssociation.mspChildWizard.override.credentialsStep';
const CRED_ERRORS = `${CREDENTIALS}.errors`;
const PRESCREEN = 'tenantsAssociation.mspChildPreScreen';

interface OverrideCredentialsStepProps {
  values: OverrideCredentials;
  onChange: (next: OverrideCredentials) => void;
  uuidConfirmed: boolean;
  onUuidConfirmedChange: (v: boolean) => void;
  children?: ReactNode;
}

const OverrideCredentialsStep = ({
  values,
  onChange,
  uuidConfirmed,
  onUuidConfirmedChange,
  children
}: OverrideCredentialsStepProps) => {
  const { t } = useTranslation('activity');
  const classes = createClasses();

  const { touched, markTouched } = useTouchedFields<OverrideCredentialField>({
    accountUuid: !!values.accountUuid,
    clientId: !!values.clientId,
    secretKey: !!values.secretKey
  });

  const update = (patch: Partial<OverrideCredentials>) =>
    onChange({ ...values, ...patch });

  const accountUuidError =
    touched.accountUuid && !isUuidValid(values.accountUuid);
  const clientIdError = touched.clientId && !isClientIdValid(values.clientId);
  const secretKeyError =
    touched.secretKey && !isSecretKeyValid(values.secretKey);

  const requiredError = t(`${CRED_ERRORS}.REQUIRED`);

  return (
    <Box className={classes.container}>
      <Box className={classes.warningBox}>
        <WarningAttentionBox title={t(`${PRESCREEN}.WARNING_TITLE`)} />
      </Box>

      <Typography variant="subtitle1" className={classes.sectionTitle}>
        {t(`${CREDENTIALS}.SECTION_TITLE`)}
      </Typography>

      <Box>
        <TextField
          value={values.accountUuid}
          label={t(`${CREDENTIALS}.ACCOUNT_UUID_LABEL`)}
          required
          placeholder={t(`${CREDENTIALS}.ACCOUNT_UUID_PLACEHOLDER`)}
          onChange={e => update({ accountUuid: e.target.value })}
          onBlur={() => markTouched('accountUuid')}
          error={accountUuidError}
          helperText={
            accountUuidError ? t(`${CREDENTIALS}.ACCOUNT_UUID_ERROR`) : ''
          }
          fullWidth
        />
        <Typography variant="caption" className={classes.uuidHint}>
          {t(`${CREDENTIALS}.ACCOUNT_UUID_HINT`)}
        </Typography>
      </Box>

      <InputLabel className={classes.checkboxLabel}>
        <Checkbox
          checked={uuidConfirmed}
          onChange={e => onUuidConfirmedChange(e.target.checked)}
        />
        <Typography variant="body2">
          {t(`${CREDENTIALS}.UUID_CONFIRMATION_CHECKBOX`)}
        </Typography>
      </InputLabel>

      <TextField
        value={values.clientId}
        label={t(`${CREDENTIALS}.CLIENT_ID_LABEL`)}
        placeholder={t(`${CREDENTIALS}.CLIENT_ID_PLACEHOLDER`)}
        onChange={e => update({ clientId: e.target.value })}
        onBlur={() => markTouched('clientId')}
        error={clientIdError}
        helperText={clientIdError ? requiredError : ''}
        fullWidth
      />

      <MaskedTextField
        value={values.secretKey}
        onChange={secretKey => update({ secretKey })}
        label={t(`${CREDENTIALS}.SECRET_KEY_LABEL`)}
        placeholder={t(`${CREDENTIALS}.SECRET_KEY_PLACEHOLDER`)}
        onBlur={() => markTouched('secretKey')}
        error={secretKeyError}
        helperText={secretKeyError ? requiredError : ''}
        showAriaLabel={t(`${CREDENTIALS}.SHOW_SECRET_KEY`)}
        hideAriaLabel={t(`${CREDENTIALS}.HIDE_SECRET_KEY`)}
      />

      {children}
    </Box>
  );
};

export default OverrideCredentialsStep;
